import { Box, Button, Container, Divider, Flex, Text } from "@chakra-ui/react";
import Head from "next/head";
import Link from "next/link";
import { getStoryblokApi } from "@storyblok/react";
import { IconChevronRight } from "../../styles/icons";
import ArticleSidebar from "../../src/articles/ArticleSidebar";
import ArticlePreviewList from "../../src/articles/ArticlePreviewList";
import ArticleReviewPanel from "../../src/articles/ArticleReviewPanel";
import ArticleAuthorBlurb from "../../src/articles/ArticleAuthorBlurb";
import { useEffect, useState } from "react";
import LoadingSpinner from "../../src/components/common/LoadingSpinner";
import { useRouter } from "next/router";

// Fetch content from Storyblok
export async function getStaticProps({ params, draftMode }) {
  const { author } = await params;

  const storyblokApi = getStoryblokApi();
  let articles = {};
  try {
    var {
      data: { story: authorData },
    } = await storyblokApi.get(`cdn/stories/authors/${author}`);
  } catch (e) {
    console.error(`Cannot retrieve author ${author}`);
    console.error(e);
    return {
      notFound: true,
    };
  }
  return {
    props: {
      draftMode: draftMode || null,
      articles,
      authorData,
    },
    revalidate: draftMode ? undefined : 3600,
  };
}

export async function getStaticPaths() {
  const storyblokApi = getStoryblokApi();

  try {
    var authors = await storyblokApi.getAll(`cdn/links`, {
      starts_with: "authors",
      version: "published",
    });
  } catch (e) {
    console.error(e);
    return {
      paths: [],
      fallback: "blocking",
    };
  }
  const paths = [];
  authors.forEach(({ is_folder, slug }) => {
    if (is_folder || slug === "home") {
      return;
    }

    paths.push({ params: { author: slug.replace(/^\/?authors\//, "") } });
  });
  return {
    paths,
    fallback: "blocking",
  };
}

export default function Author({ authorData, showMore }) {
  const storyblokApi = getStoryblokApi();
  const router = useRouter();
  const title = `Explore our articles written by ${authorData.name}`;
  const description = `Explore our articles written by ${authorData.name}. We have posts from writers, therapists, and people like you. Contact us if want to share your experience of therapy.`;

  const [articles, setArticles] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const searchArticles = async () => {
      setLoading(true);
      let articlesData = {};
      if (!articles[currentPage]) {
        const { data } = await storyblokApi.get(`cdn/stories`, {
          starts_with: "article",
          sort_by: "content.legacy_published_at:desc",
          resolve_relations: ["article.authors"],
          page: currentPage,
          per_page: 10,
          filter_query: {
            authors: {
              any_in_array: authorData.uuid,
            },
          },
          excluding_fields: "body,summary",
        });
        articlesData[currentPage] = data.stories;
      }
      if (!articles[currentPage + 1]) {
        const { data } = await storyblokApi.get(`cdn/stories`, {
          starts_with: "article",
          sort_by: "content.legacy_published_at:desc",
          resolve_relations: ["article.authors"],
          page: currentPage + 1,
          per_page: 10,
          filter_query: {
            authors: {
              any_in_array: authorData.uuid,
            },
          },
          excluding_fields: "body,summary",
        });
        articlesData[currentPage + 1] = data.stories;
      }
      setArticles((current) => ({
        ...current,
        ...articlesData,
      }));
      setLoading(false);
    };
    searchArticles();
  }, [currentPage]);

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="og:title" content={title} />
        <meta name="description" content={description} />
        <meta name="og:description" content={description} />
        <meta name="keywords" content={authorData.name} />
        <link rel="canonical" href={`https://welldoing.org${router.asPath}`} />
      </Head>
      <Container
        maxW={{ base: "100%", xl: "85%" }}
        boxSizing="border-box"
        overflowX="hidden"
      >
        <Box width="100%" display="flex">
          <Box mx="auto" my="50px" width="100%">
            <Flex align="center" gap="10px">
              <Link href="/" legacyBehavior>
                <Text color="brand.400" cursor="pointer">{`Homepage`}</Text>
              </Link>
              <IconChevronRight size="22px" />
              <Link href="/article" legacyBehavior>
                <Text
                  color="brand.400"
                  cursor="pointer"
                >{`Welldoing Articles`}</Text>
              </Link>
              <IconChevronRight size="22px" />
              <Text>{authorData.name}</Text>
            </Flex>
            <Box my="50px">
              <Flex columnGap="120px">
                <Flex flex={2} direction="column">
                  <Flex
                    justify="space-between"
                    direction={{ base: "column", xl: "row" }}
                  ></Flex>
                  <ArticleAuthorBlurb
                    key={authorData.uuid}
                    image={authorData?.content?.image?.filename}
                    firstName={authorData?.content.firstName}
                    lastName={authorData?.content.lastName}
                    middleNames={authorData?.content.middleNames}
                    title={authorData?.content?.subtitle}
                    summary={authorData?.content?.description}
                  />
                  <Divider borderWidth="1px" mt="40px" />
                  {loading ? (
                    <>
                      <LoadingSpinner />
                    </>
                  ) : (
                    articles[currentPage].map((article) => {
                      return (
                        <ArticlePreviewList
                          key={article.id}
                          storyblokImage={article.content.headerImage}
                          title={article.content.title}
                          author={authorData.name}
                          slug={article.slug}
                          date={
                            article.content.legacy_published_at !== ""
                              ? article.content.legacy_published_at
                              : article.first_published_at
                          }
                        />
                      );
                    })
                  )}
                  <Flex direction="row">
                    {currentPage > 1 && (
                      <Button
                        w="145px"
                        mt="40px"
                        onClick={() => {
                          setCurrentPage((current) => current - 1);
                          window.scrollTo({
                            top: 0,
                            behavior: "smooth",
                          });
                        }}
                      >
                        Previous Page
                      </Button>
                    )}
                    {articles[currentPage + 1]?.length > 0 && (
                      <Button
                        w="145px"
                        mt="40px"
                        onClick={() => {
                          setCurrentPage((current) => current + 1);
                          window.scrollTo({
                            top: 0,
                            behavior: "smooth",
                          });
                        }}
                      >
                        Next Page
                      </Button>
                    )}
                  </Flex>
                  <Flex display={{ base: "flex", xl: "none" }}>
                    <ArticleReviewPanel />
                  </Flex>
                </Flex>

                <ArticleSidebar
                  overrideVideoId={
                    authorData.content.overrideSidebarVideoId ?? null
                  }
                />
              </Flex>
            </Box>
          </Box>
        </Box>
      </Container>
    </>
  );
}
